import { useFormik } from 'formik'
import { Modal } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import { Attribute } from './SectionComponent'
import { createOne, getAll } from '../../../../services/common_requests'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useEffect, useState } from 'react'
import { useQueryRequest } from '../../pages-list/core/QueryRequestProvider'
import { QUERIES } from '../../../../../_dashboard/helpers'
import { useQuery, useQueryClient } from 'react-query'
import * as Yup from 'yup';
import clsx  from 'clsx';

interface AddAttributeModalProps {
    show: boolean
    onHide: () => void
    onAdd: (attribute: Omit<Attribute, '_id'> & { blockId: string }) => void
    blockId: string
    pageId: string 
    initialValues?: Omit<Attribute, '_id'>
}

interface AddAttributeFormValues {
    type: string
    buttonLink?: string
    image?: File
    seoUrl?: string
    key: string
    translations: Array<{
        _id: string
        language: string
        value: string
        label: string
    }>
    imageFile?: File
    attributes: Attribute[]
}

export const AddAttributeModal: React.FC<AddAttributeModalProps> = ({
    show,
    onHide,
    onAdd,
    blockId,
    pageId, 
    initialValues = {
        type: 'text',
        key: '',
        attributes: [],
        translations: [
            {
                _id: Date.now().toString(),
                language: 'fr',
                value: '',
                label: '',
            },
        ],
    },
}) => {
    const intl = useIntl()
    const [languages, setLanguages] = useState<{ code: string; name: string }[]>([])
    const [selectedLanguage, setSelectedLanguage] = useState('en')
    const LANGUAGE_NAMES: { [key: string]: string } = {
        en: useIntl().formatMessage({ id: 'COMMON.ENGLISH_LANGUAGE' }),
        fr: useIntl().formatMessage({ id: 'COMMON.FRENSH_LANGUAGE' }),
        es: useIntl().formatMessage({ id: 'COMMON.ESPANGOL_LANGUAGE' }),
        ar: useIntl().formatMessage({ id: 'COMMON.ARABIC_LANGUAGE' }),
    }
    const { state } = useQueryRequest()
    const [query, setQuery] = useState<any>(state)
    useEffect(() => {
        getAll(query, '/languages').then((data) => {
            if (data && Array.isArray(data)) {
                const formattedLanguages = data.map((code: string) => ({
                    code,
                    name: LANGUAGE_NAMES[code] || code.toUpperCase(),
                }))
                setLanguages(formattedLanguages)
            }
        })
    }, [query])
    const queryClient = useQueryClient()

    const attributeValidationSchema = Yup.object().shape({
        type: Yup.string().required(intl.formatMessage({ id: 'ERROR.REQUIRED_TYPE' })),
       // key: Yup.string().required(intl.formatMessage({ id: 'ERROR.REQUIRED_KEY' })),
        translations: Yup.array().when('type', {
            is: (type: string) => type !== 'image',
            then: Yup.array().of(
                Yup.object().shape({
                    label: Yup.string().required(intl.formatMessage({ id: 'ERROR.REQUIRED_LABEL' })),
                    value: Yup.string().required(intl.formatMessage({ id: 'ERROR.REQUIRED_VALUE' }))
                })
            ).required()
        }),
        buttonLink: Yup.string()
            .when('type', {
                is: 'button',
                then: Yup.string()
                   // .url(intl.formatMessage({ id: 'ERROR.INVALID_URL' }))
                    .required(intl.formatMessage({ id: 'ERROR.REQUIRED_BUTTON_LINK' }))
            }),
    });

    const formik = useFormik<AddAttributeFormValues>({
        validationSchema: attributeValidationSchema,
        initialValues: {
            type: 'text',
            key: '',
            attributes: [],
            translations: [
                {
                    _id: Date.now().toString(),
                    language: 'fr',
                    value: '',
                    label: '',
                },
            ],
            buttonLink: '',
        },
        onSubmit: async (values, { resetForm }) => {
            try {
                const formData = new FormData();
                formData.append('type', values.type);
                formData.append('blockId', blockId);
                formData.append('key', values.key);
                formData.append('buttonLink', values.buttonLink || '');

        
                if (values.type === 'image' && values.image) {
                    formData.append('image', values.image);
                }
        
                if (values.type !== 'image' && values.translations) {
                    values.translations.forEach((translation, index) => {
                        formData.append(`translations[${index}][language]`, translation.language);
                        formData.append(`translations[${index}][value]`, translation.value);
                        formData.append(`translations[${index}][label]`, translation.label);
                    });
                }
        
                const response = await createOne(formData, '/attributes');
        
                if (response?.payload?.code === 2021) {
                    toast.success(
                        intl.formatMessage({
                            id: 'ADMIN.COMMON.SUCCESS_ADDING_ATTRIBUTE_SUCCESSUFULY',
                        }),
                        {
                            position: 'top-right',
                            autoClose: 4000,
                        }
                    );
                    const newAttribute = {
                        ...values,
                        _id: response.data.attribute._id,
                        blockId: blockId,
                        image: values.type === 'image' && values.image ? { 
                            _id: response.data.attribute.image,        
                            url: URL.createObjectURL(values.image),
                            seoUrl: values.seoUrl || '' 
                        } : undefined,
                        ...(values.type !== 'image' && {
                            translations: values.translations?.map((translation, index) => 
                                index === 0 ? { ...translation, _id: response.data.createdIds[0] } : translation
                            )
                        })
                    };
                    
                    

                    onHide()
                    resetForm()
                    onAdd(newAttribute)
        
                  
                } else {
                    // Gestion des erreurs
                    toast.error(
                        intl.formatMessage({
                            id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_ATTRIBUTE',
                        }),
                        {
                            position: 'top-right',
                            autoClose: 4000,
                        }
                    );
                }
            } catch (ex) {
                console.error(ex);
                queryClient.invalidateQueries([QUERIES.SECTION_LIST, pageId, selectedLanguage]);
            }
        }
    })

    useEffect(() => {
        if (show) {
            formik.resetForm()
        }
    }, [show])

    return (
        <Modal show={show} onHide={onHide} centered>
            <form onSubmit={formik.handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title>{intl.formatMessage({ id: 'ADMIN.ATTRIBUTE.ADD' })}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {formik.values.type !== 'image' && (
                        <div className='fv-row mb-4'>
                            <label className='required fw-bold fs-6 mb-2'>
              {intl.formatMessage({ id: 'COLUMNS.LANGUAGE' })}
            </label>
            {formik.values.translations.map((translation, index) => (
              <div key={index}>
                <select
                  id={`translations[${index}].language`}
                  name={`translations[${index}].language`}
                  onChange={(e) => {
                    formik.handleChange(e)
                    setSelectedLanguage(e.target.value) // Met à jour la langue sélectionnée
                  }}
                  value={translation.language}
                  className='form-select'
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
                {formik.errors.translations &&
                  (formik.errors.translations[index] as { language?: string })?.language && (
                    <div>{(formik.errors.translations[index] as { language: string }).language}</div>
                  )}
              </div>
            ))}
                        </div>
                    )}
                    <div className='mb-3'>
                        <label className='required fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'ADMIN.ATTRIBUTE.TYPE' })}</label>
                        <select
                            className='form-select'
                            name='type'
                            value={formik.values.type}
                            onChange={formik.handleChange}
                        >
                            <option value='text'>Texte</option>
                            <option value='long-text'>Texte long</option>
                            <option value='image'>Image</option>
                            <option value='button'>Bouton</option>
                        </select>
                    </div>

                    {formik.values.translations.map((translation, index) => (
                        <div key={index}>
                            {formik.values.type !== 'image' && (
                                <>
                                    <div className='mb-3'>
                                        <label className='required fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'COLUMNS.LABEL' })}</label>
                                        <input
                                            type='text'
                                            className={clsx(
                                                'form-control',
                                                { 
                                                    'is-invalid': formik.touched.translations?.[index]?.label && 
                                                                (formik.errors.translations?.[index] as any)?.label 
                                                }
                                            )}
                                            name={`translations[${index}].label`}
                                            value={translation.label}
                                            onChange={formik.handleChange}
                                        />
                                        {formik.touched.translations && 
                                         (formik.errors.translations?.[index] as { label?: string })?.label && (
                                            <div className='text-danger mt-1'>
                                                {(formik.errors.translations?.[index] as { label?: string })?.label}
                                            </div>
                                        )}
                                    </div>

                                    <div className='mb-3'>
                                        <label className='required fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'COLUMNS.VALUE' })}</label>
                                        <input
                                            type='text'
                                            className={clsx(
                                                'form-control',
                                                { 
                                                    'is-invalid': formik.touched.translations && 
                                                                (formik.errors.translations?.[index] as { value?: string })?.value 
                                                }
                                            )}
                                            name={`translations[${index}].value`}
                                            value={translation.value}
                                            onChange={formik.handleChange}
                                        />
                                        {formik.touched.translations && 
                                         (formik.errors.translations?.[index] as { value?: string })?.value && (
                                            <div className='text-danger mt-1'>
                                                {(formik.errors.translations?.[index] as { value?: string })?.value}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    ))}

                    <input type='hidden' name='translations[0].language' value='fr' />
                    {formik.values.type === 'button' && (
                        <div className='mb-3'>
                            <label className='required fw-bold fs-6 mb-2'>
                                {intl.formatMessage({ id: 'ADMIN.ATTRIBUTE.BTN_LINK' })}
                            </label>
                            <input
                                type='text'
                                className={clsx(
                                    'form-control',
                                    { 
                                        'is-invalid': (formik.touched.buttonLink || formik.submitCount > 0) && 
                                                    formik.errors.buttonLink 
                                    }
                                )}
                                name='buttonLink'
                                value={formik.values.buttonLink || ''}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder='https://example.com'
                            />
                            {(formik.touched.buttonLink || formik.submitCount > 0) && formik.errors.buttonLink && (
                                <div className='text-danger mt-1'>{formik.errors.buttonLink}</div>
                            )}
                        </div>
                    )}
                    {formik.values.type === 'image' && (
                        <>
                            <div className='mb-3'>
                                <label className='required fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'ADMIN.ATTRIBUTE.IMAGE_UPLOAD' })}</label>
                                <input
                                    className={clsx(
                                        'form-control',
                                        { 'is-invalid': formik.touched.image && formik.errors.image }
                                    )}
                                    type="file"
                                    accept=".jpg,.jpeg,.png"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0]
                                        if (file) {
                                            const extension = file.name.split('.').pop()?.toLowerCase()
                                            if (!['jpg', 'jpeg', 'png'].includes(extension || '')) {
                                                toast.error(
                                                    intl.formatMessage({
                                                        id: 'COMMUN.ALLOWED_FILE',
                                                    }),
                                                    {
                                                        position: 'top-right',
                                                        autoClose: 4000,
                                                    }
                                                );
                                                return
                                            }
                                            formik.setFieldValue('image', file)
                                            formik.setFieldTouched('image', true)
                                        }
                                    }}
                                />
                                {formik.touched.image && formik.errors.image && (
                                    <div className='text-danger mt-1'>{formik.errors.image}</div>
                                )}
                                {formik.values.imageFile && (
                                    <img
                                        src={URL.createObjectURL(formik.values.imageFile)}
                                        alt='Preview'
                                        className='mt-2 img-thumbnail'
                                        style={{ maxWidth: '200px' }}
                                    />
                                )}
                            </div>
                            <div className='mt-3'>
                                <label className='fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'COLUMNS.SEOURL' })}</label> 
                                <input
                                    type='text'
                                    className={clsx(
                                        'form-control',
                                        { 'is-invalid': formik.touched.seoUrl && formik.errors.seoUrl }
                                    )}
                                    value={formik.values.seoUrl || ''}
                                    onChange={(e) => {
                                        formik.setFieldValue('seoUrl', e.target.value)
                                        formik.setFieldTouched('seoUrl', true)
                                    }}
                                    placeholder=''
                                />
                                {formik.touched.seoUrl && formik.errors.seoUrl && (
                                    <div className='text-danger mt-1'>{formik.errors.seoUrl}</div>
                                )}
                            </div>
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <button type='button' className='btn btn-secondary' onClick={onHide}>
                        {intl.formatMessage({ id: 'ACTION.CANCEL' })}
                    </button>
                    <button type='submit' className='btn btn-primary'>
                        {intl.formatMessage({ id: 'ACTION.ADD' })}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    )
}
